import { CONFIG_PROPERTY_KEYS } from "./constants";

/**
 * Configuration utility functions for accessing configuration values
 * These functions provide type-safe access to configuration properties
 */

/**
 * Get a configuration value with type conversion
 * @param {Object} configurationData - The configuration data object
 * @param {string} propertyKey - The property key to retrieve
 * @param {*} defaultValue - Default value if property not found
 * @param {string} type - Type conversion ('string', 'number', 'boolean', 'array')
 * @returns {*} The converted configuration value
 */
export const getConfigValue = (configurationData, propertyKey, defaultValue = null, type = 'string') => {
  if (!configurationData || !propertyKey) return defaultValue;
  
  const config = configurationData[propertyKey];
  if (!config) return defaultValue;
  
  const value = config.value;
  
  switch (type) {
    case 'number':
      return value ? parseFloat(value) : defaultValue;
    case 'boolean':
      return value === 'true' || value === true;
    case 'array':
      return value ? value.split(',').map(item => item.trim()) : defaultValue;
    case 'string':
    default:
      return value || defaultValue;
  }
};

/**
 * Get numeric configuration value
 * @param {Object} configurationData - The configuration data object
 * @param {string} propertyKey - The property key to retrieve
 * @param {number} defaultValue - Default value if property not found
 * @returns {number} The numeric configuration value
 */
export const getNumericConfig = (configurationData, propertyKey, defaultValue = 0) => {
  return getConfigValue(configurationData, propertyKey, defaultValue, 'number');
};

/**
 * Get boolean configuration value
 * @param {Object} configurationData - The configuration data object
 * @param {string} propertyKey - The property key to retrieve
 * @param {boolean} defaultValue - Default value if property not found
 * @returns {boolean} The boolean configuration value
 */
export const getBooleanConfig = (configurationData, propertyKey, defaultValue = false) => {
  return getConfigValue(configurationData, propertyKey, defaultValue, 'boolean');
};

/**
 * Get array configuration value
 * @param {Object} configurationData - The configuration data object
 * @param {string} propertyKey - The property key to retrieve
 * @param {Array} defaultValue - Default value if property not found
 * @returns {Array} The array configuration value
 */
export const getArrayConfig = (configurationData, propertyKey, defaultValue = []) => {
  return getConfigValue(configurationData, propertyKey, defaultValue, 'array');
};

/**
 * Get string configuration value
 * @param {Object} configurationData - The configuration data object
 * @param {string} propertyKey - The property key to retrieve
 * @param {string} defaultValue - Default value if property not found
 * @returns {string} The string configuration value
 */
export const getStringConfig = (configurationData, propertyKey, defaultValue = '') => {
  return getConfigValue(configurationData, propertyKey, defaultValue, 'string');
};

/**
 * Specific configuration getters for common properties
 */
export const getBufferHours = (configurationData) => {
  return getNumericConfig(configurationData, CONFIG_PROPERTY_KEYS.BUFFER_HOURS, 1);
};

export const getVAT = (configurationData) => {
  return getNumericConfig(configurationData, CONFIG_PROPERTY_KEYS.VAT, 0.10);
};

export const getEscortRequestCutoffHours = (configurationData) => {
  return getNumericConfig(configurationData, CONFIG_PROPERTY_KEYS.ESCORT_REQUEST_CUTOFF_HOURS, 36);
};

export const getOvertimeRequestCutoffHours = (configurationData) => {
  return getNumericConfig(configurationData, CONFIG_PROPERTY_KEYS.OVERTIME_REQUEST_CUTOFF_HOURS, 24);
};

export const getMinimumEscortOfficers = (configurationData) => {
  return getNumericConfig(configurationData, CONFIG_PROPERTY_KEYS.MINIMUM_ESCORT_OFFICERS, 2);
};

export const getMinimumEscortOfficerHours = (configurationData) => {
  return getNumericConfig(configurationData, CONFIG_PROPERTY_KEYS.MINIMUM_ESCORT_OFFICER_HOURS, 2);
};

export const getMinimumOvertimeOfficers = (configurationData) => {
  return getNumericConfig(configurationData, CONFIG_PROPERTY_KEYS.MINIMUM_OVERTIME_OFFICERS, 2);
};

export const getMinimumOvertimeOfficerHours = (configurationData) => {
  return getNumericConfig(configurationData, CONFIG_PROPERTY_KEYS.MINIMUM_OVERTIME_OFFICER_HOURS, 2);
};

export const getMinimumSeatCapacityForCrew = (configurationData) => {
  return getNumericConfig(configurationData, CONFIG_PROPERTY_KEYS.MINIMUM_SEAT_CAPACITY_FOR_CREW, 20);
};

export const getTolerance = (configurationData) => {
  return getNumericConfig(configurationData, CONFIG_PROPERTY_KEYS.TOLERANCE, 2);
};

export const getFlightApprovalDelay = (configurationData) => {
  return getNumericConfig(configurationData, CONFIG_PROPERTY_KEYS.FLIGHT_APPROVAL_DELAY, 2);
};

export const getGovernmentPassenger = (configurationData) => {
  return getBooleanConfig(configurationData, CONFIG_PROPERTY_KEYS.GOVERNMENT_PASSENGER, false);
};

export const getDiplomatsPassenger = (configurationData) => {
  return getBooleanConfig(configurationData, CONFIG_PROPERTY_KEYS.DIPLOMATS_PASSENGER, false);
};

export const getMilitaryPassenger = (configurationData) => {
  return getBooleanConfig(configurationData, CONFIG_PROPERTY_KEYS.MILITARY_PASSENGER, false);
};

export const getInfantPassenger = (configurationData) => {
  return getBooleanConfig(configurationData, CONFIG_PROPERTY_KEYS.INFANT_PASSENGER, false);
};

/**
 * Bank configuration getters
 */
export const getBSDAccountName = (configurationData) => {
  return getStringConfig(configurationData, CONFIG_PROPERTY_KEYS.BSD_ACCOUNT_NAME, 'The Airport Authority');
};

export const getBSDBankName = (configurationData) => {
  return getStringConfig(configurationData, CONFIG_PROPERTY_KEYS.BSD_BANK_NAME, 'Royal Bank of Canada');
};

export const getBSDBranchNumber = (configurationData) => {
  return getStringConfig(configurationData, CONFIG_PROPERTY_KEYS.BSD_BRANCH_NUMBER, '05705');
};

export const getBSDAccountNumber = (configurationData) => {
  return getStringConfig(configurationData, CONFIG_PROPERTY_KEYS.BSD_ACCOUNT_NUMBER, '1003391');
};

export const getUSDIntermediaryBank = (configurationData) => {
  return getStringConfig(configurationData, CONFIG_PROPERTY_KEYS.USD_INTERMEDIARY_BANK, 'JP Morgan Chase Bank, New York');
};

export const getUSDAba = (configurationData) => {
  return getStringConfig(configurationData, CONFIG_PROPERTY_KEYS.USD_ABA, '021000021');
};

export const getUSDSwift = (configurationData) => {
  return getStringConfig(configurationData, CONFIG_PROPERTY_KEYS.USD_SWIFT, 'CHASUS33');
};

export const getUSDCreditAccount = (configurationData) => {
  return getStringConfig(configurationData, CONFIG_PROPERTY_KEYS.USD_CREDIT_ACCOUNT, '01118448');
};

export const getUSDBeneficiaryBank = (configurationData) => {
  return getStringConfig(configurationData, CONFIG_PROPERTY_KEYS.USD_BENEFICIARY_BANK, 'RBC Royal Bank Bahamas Limited, Nassau');
};

export const getUSDBahamasTransitNumber = (configurationData) => {
  return getStringConfig(configurationData, CONFIG_PROPERTY_KEYS.USD_BAHAMAS_TRANSIT_NUMBER, '05705');
};

export const getUSDAccountNumber = (configurationData) => {
  return getStringConfig(configurationData, CONFIG_PROPERTY_KEYS.USD_ACCOUNT_NUMBER, '4003190');
};

export const getUSDBeneficiaryName = (configurationData) => {
  return getStringConfig(configurationData, CONFIG_PROPERTY_KEYS.USD_BENEFICIARY_NAME, 'The Airport Authority');
};

/**
 * Cutoff hours getters
 */
export const getEscortUnapprovedRejectionCutoffHours = (configurationData) => {
  return getNumericConfig(configurationData, CONFIG_PROPERTY_KEYS.ESCORT_UNAPPROVED_REJECTION_CUTOFF_HOURS, 24);
};

export const getEscortUnpaidRejectionCutoffHours = (configurationData) => {
  return getNumericConfig(configurationData, CONFIG_PROPERTY_KEYS.ESCORT_UNPAID_REJECTION_CUTOFF_HOURS, 12);
};

export const getEscortCancellationCutoffHours = (configurationData) => {
  return getNumericConfig(configurationData, CONFIG_PROPERTY_KEYS.ESCORT_CANCELLATION_CUTOFF_HOURS, 12);
};

export const getOvertimeUnapprovedRejectionCutoffHours = (configurationData) => {
  return getNumericConfig(configurationData, CONFIG_PROPERTY_KEYS.OVERTIME_UNAPPROVED_REJECTION_CUTOFF_HOURS, 24);
};

export const getOvertimeUnpaidRejectionCutoffHours = (configurationData) => {
  return getNumericConfig(configurationData, CONFIG_PROPERTY_KEYS.OVERTIME_UNPAID_REJECTION_CUTOFF_HOURS, 12);
};

/**
 * Validate if configuration data is available
 * @param {Object} configurationData - The configuration data object
 * @returns {boolean} True if configuration data is available
 */
export const isConfigurationLoaded = (configurationData) => {
  return configurationData && Object.keys(configurationData).length > 0;
};

/**
 * Get all configuration values as a flat object
 * @param {Object} configurationData - The configuration data object
 * @returns {Object} Object with property keys as keys and values as values
 */
export const getAllConfigValues = (configurationData) => {
  if (!configurationData) return {};
  
  const result = {};
  Object.keys(configurationData).forEach(key => {
    result[key] = configurationData[key].value;
  });
  return result;
};
